import React, { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { Collapse, Grid } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { useDndEquipmentOrderItems } from './hooks/useDndEquipmentOrderItems';
import EquipmentOrderGroupHeader from './components/EquipmentOrderGroupHeader/EquipmentOrderGroupHeader';
import EquipmentOrderGroupItem from './components/EquipmentOrderGroupItem/EquipmentOrderGroupItem';
import styles from './styles';
import { gridScheduleOrderCollision } from 'common/dnd-collision-detectors';

function numberToSymbol(num) {
  if (typeof num !== 'number' || num < 0) return '';
  let s = '';
  num = Math.floor(num);
  while (num >= 0) {
    s = String.fromCharCode((num % 26) + 65) + s;
    num = Math.floor(num / 26) - 1;
  }
  return s;
}

function EquipmentOrderList({
  classes,
  equipment = [],
  colorGroups,
  equipmentOrderInColorGroups,
  onMoveEquipment,
  onMoveColorGroup,
  openConfirmDialog,
  locations,
}) {
  const [collapsed, setCollapsed] = useState({});

  const {
    dropIndicator,
    groups,
    sortedGroups,
    activeGroup,
    activeEquipment,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handleDragMove,
  } = useDndEquipmentOrderItems({
    colorGroups,
    equipmentOrderInColorGroups,
    equipment,
    locations,
    onMoveEquipment,
    onMoveColorGroup,
    openConfirmDialog,
  });

  const toggleCollapse = useCallback((color) => {
    setCollapsed((prev) => ({ ...prev, [color]: !prev[color] }));
  }, []);

  return (
    <div className={classes.listRoot}>
      <DndContext
        sensors={sensors}
        collisionDetection={gridScheduleOrderCollision}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={sortedGroups} strategy={verticalListSortingStrategy}>
          {sortedGroups.map((group, index) => (
            <div key={group._id} style={{ marginBottom: 16 }}>
              <EquipmentOrderGroupHeader group={group} groupName={`Group ${numberToSymbol(group.orderNumber)}`} collapsed={!!collapsed[group.color]} onClick={toggleCollapse} />
              <Collapse in={!collapsed[group.color]} timeout="auto" unmountOnExit>
                <Grid container spacing={2} justifyContent="flex-start" style={{ paddingBottom: 20 }}>
                  <SortableContext items={groups[group.color] || []} strategy={rectSortingStrategy}>
                    {(groups[group.color] || []).map((equipmentOrder, idx, arr) => (
                      <React.Fragment key={equipmentOrder._id}>
                        <EquipmentOrderGroupItem
                          key={equipmentOrder._id}
                          equipment={equipmentOrder.equipment}
                          data-item-id={equipmentOrder._id}
                          drawInsertionCaret={dropIndicator?.itemId === equipmentOrder.equipmentId ? dropIndicator.position : undefined}
                        />
                      </React.Fragment>
                    ))}
                  </SortableContext>
                  </Grid>
              </Collapse>
            </div>
          ))}
        </SortableContext>
        <DragOverlay>
          {activeGroup ? (
            <EquipmentOrderGroupHeader
              group={activeGroup}
              groupName={`Group ${numberToSymbol(activeGroup.orderNumber)}`}
              collapsed={!!collapsed[activeGroup.color]}
              onClick={toggleCollapse}
              id={activeGroup._id}
              dragging
            />
          ) : null}
          {activeEquipment ? (
            <EquipmentOrderGroupItem id={activeEquipment} equipment={equipment.find((eq) => eq._id === activeEquipment)} dragging />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

export default withStyles(styles)(EquipmentOrderList)